import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import { MdForum } from 'react-icons/md';
import { useSearch } from 'react-location';
import { createLogger } from '../../modules/prelude/logger';
import shl_content_image from './../../../assets/shl_content_image.png';
import { useCreateWebchat } from '../../modules/datastores/useCreateWebchat';
import AdditionalTerms from './AdditionalTerms';
// import { taskIdPattern } from '../../modules/prelude/constants';
import Cookies from 'universal-cookie';
import type { UseQueryResult } from 'react-query';
import type { HotlineMetaData, HotlinePageData } from '../../modules/algebra/global';
import DOMPurify from 'dompurify';

const fileLabel = 'TOSTermsBanner.ts';
const logger = createLogger({
  fileLabel,
});

const TOSTermsBanner: FC<{ statusQuery: UseQueryResult<HotlineMetaData>; pageData: HotlinePageData }> = ({ statusQuery, pageData }) => {
  const query = useSearch();
  const messaging_service_key = query['messaging_service_key'] as string;
  const { mutateAsync: createWebchat, isLoading } = useCreateWebchat();
  const cookies = new Cookies();
  const { status } = statusQuery.data || {};
  const { bannerInfoText, bannerTitle, bannerBackgroundImage, bannerBackgroundColor, bannerContent, bannerImgUrl, bannerTitleColor, fontColor, chatNowBtnColor } = pageData;
  const redirect = (createChat: string) => {
    window.location.replace(`${process.env.NX_HOTLINES_CHAT_NOW_BASE_URL}/?messaging_service_key=${messaging_service_key}&task_sid=${createChat}`);
  };
  const startTalking = async () => {
    try {
      const createChat = await createWebchat({ messaging_service_key });
      const date = new Date();
      date.setTime(date.getTime() + 10 * 60 * 1000);
      cookies.set(`task_sid${messaging_service_key}`, createChat, { path: '/', expires: date });
      redirect(createChat);
    } catch (startChatError) {
      logger.error('Error While Starting Conversation', startChatError);
    }
  };

  // useEffect(() => {
  //   if (taskIdPattern.test(cookies.get(`task_sid${messaging_service_key}`))) {
  //     redirect(cookies.get(`task_sid${messaging_service_key}`));
  //   }
  // }, []);

  return (
    <div
      className={`bg-pc-1  py-8`}
      style={{
        color: fontColor,
        ...(bannerBackgroundImage ? { backgroundImage: `url(${bannerBackgroundImage})` } : bannerBackgroundColor ? { backgroundColor: bannerBackgroundColor } : {}),
      }}
    >
      <div className="px-4 md:px-14" style={{ color: fontColor }}>
        <Typography variant="h1" className="text-tc-4 flex justify-center text-2xl font-bold md:text-left text-center" style={{ color: bannerTitleColor || '' }}>
          {bannerTitle || `The ${process.env.NX_ORGANIZATION_NAME} Online Hotline`}
        </Typography>
        <Typography variant="body1" className="text-xl italic text-center text-tc-1 border-b border-tc-4 pb-3" style={{ color: fontColor }}>
          {bannerInfoText}
        </Typography>
      </div>
      <div className="flex flex-col lg:flex-row md:pl-8 sm:pl-0" style={{ color: fontColor }}>
        <div className="w-full lg:w-1/2 px-4 md:px-14 py-4">
          {bannerContent && <div className="mb-4 text-base" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(bannerContent, { ADD_ATTR: ['target'] }) }}></div>}
          <AdditionalTerms />
          {(() => {
            if (statusQuery.isLoading) return <CircularProgress />;
            else if (statusQuery.isSuccess && status)
              return (
                <>
                  <Button
                    startIcon={<MdForum className="w-12 h-12" />}
                    onClick={startTalking}
                    className="chat-now"
                    disabled={isLoading}
                    style={{ color: chatNowBtnColor, borderColor: chatNowBtnColor }}
                  >
                    Chat Now
                  </Button>
                  <Typography variant="h2" className="font-bold pt-3 text-lg" style={{ color: fontColor }}>
                    By clicking "Chat Now," you are agreeing to our&nbsp;
                    <a href="https://rainn.org/terms-service" target="_blank" className="text-tc-1 text-underline" style={{ color: fontColor }} rel="noreferrer">
                      terms of service
                    </a>
                    .
                  </Typography>
                </>
              );
            else
              return (
                <Typography variant="h2" className="text-tc-1 font-bold pt-3 text-lg" style={{ color: fontColor }}>
                  Currently All our Operators Are Busy
                </Typography>
              );
          })()}
        </div>
        <div className="w-full sm:w-1/2 px-4 md:px-14 py-4 mt-2">
          <div
            className="video"
            style={{
              position: 'relative',
              paddingBottom: '56.25%' /* 16:9 */,
              paddingTop: 25,
              height: 0,
            }}
          >
            <img
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
              src={bannerImgUrl || shl_content_image}
              alt="Some Content"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TOSTermsBanner;
